import React, { ReactNode, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { enqueueSnackbar } from 'notistack';
import { supabase } from '../lib/supabase';
import NotificationCenter from './NotificationCenter';
import RoleBasedNavigation from './RoleBasedNavigation';
import UserProfileMenu from './UserProfileMenu';
import { RoleSwitcher } from './RoleSwitcher';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MarketingFooter from '../marketing/components/Footer';
import Logo from './brand/Logo';
import { brand } from '../theme';
import { ComplianceAlertBanner } from './ComplianceAlertBanner';
import { useImpersonationExpiryWatcher } from '../hooks/useImpersonationExpiryWatcher';
import { useServiceWorkerNavigation } from '../hooks/useServiceWorkerNavigation';
import { setAnalyticsUser, trackPageView } from '../lib/analytics';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  useServiceWorkerNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, isLoading } = useAuth();
  // Ends the banner (with a snackbar) when the database stops honouring the impersonation.
  const { markExplicitStop } = useImpersonationExpiryWatcher(user, refreshUser);

  // Listen for background AI intake request if user is not on the jobs/new page
  useEffect(() => {
    if (!user) return;
    if (location.pathname === '/dashboard/jobs/new') return;

    const activeRequestId = sessionStorage.getItem('active_ai_intake_request_id');
    if (!activeRequestId) return;

    const channel = supabase
      .channel(`global_job_intake_request:${activeRequestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'job_intake_requests',
          filter: `id=eq.${activeRequestId}`,
        },
        (payload) => {
          const updatedRow = payload.new;
          if (updatedRow.status === 'COMPLETED') {
            // A finished intake is no longer running, so the next new job starts clean. "Return to
            // Form" hands this one back to the form, which applies it (unless another has started).
            const draftJobId = sessionStorage.getItem('active_ai_draft_job_id');
            supabase.removeChannel(channel);
            sessionStorage.removeItem('active_ai_intake_request_id');
            sessionStorage.removeItem('active_ai_draft_job_id');
            enqueueSnackbar('AI has finished analyzing your job brief!', {
              variant: 'success',
              action: () => (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    if (draftJobId && !sessionStorage.getItem('active_ai_intake_request_id')) {
                      sessionStorage.setItem('active_ai_intake_request_id', activeRequestId);
                      sessionStorage.setItem('active_ai_draft_job_id', draftJobId);
                    }
                    navigate('/dashboard/jobs/new');
                  }}
                  sx={{ fontWeight: 'bold' }}
                >
                  Return to Form
                </Button>
              ),
              persist: true,
            });
          } else if (updatedRow.status === 'FAILED') {
            supabase.removeChannel(channel);
            sessionStorage.removeItem('active_ai_intake_request_id');
            sessionStorage.removeItem('active_ai_draft_job_id');
            enqueueSnackbar(updatedRow.error_message || 'AI Job Intake parsing failed.', {
              variant: 'error',
            });
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          supabase.removeChannel(channel);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, location.pathname, navigate]);

  const handleStopImpersonating = async () => {
    markExplicitStop(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.rpc('stop_impersonating_user');
      if (error) {
        throw error;
      }
      await refreshUser();
      enqueueSnackbar('Stopped impersonating successfully', { variant: 'success' });
      navigate('/admin/users');
    } catch (err: unknown) {
      markExplicitStop(false);
      if (import.meta.env.DEV) {
        console.error('Failed to stop impersonating:', err);
      }
      const msg = err instanceof Error ? err.message : 'Failed to stop impersonating';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const isAboutActive = normalizedPath === '/about-us' || normalizedPath === '/about';

  const isMarketingRoute = normalizedPath === '/' ||
    normalizedPath.startsWith('/for-') ||
    normalizedPath.startsWith('/signup') ||
    normalizedPath === '/login' ||
    normalizedPath.startsWith('/news') ||
    normalizedPath.startsWith('/jobs/teasers') ||
    normalizedPath === '/about-us' ||
    normalizedPath === '/about';

  // Analytics waits for auth to resolve: before that, `user` is null even for staff, and loading the
  // tag would let gtag's own scroll events go out as an anonymous visitor.
  useEffect(() => {
    if (!isLoading) {
      setAnalyticsUser(user);
    }
  }, [user, isLoading]);

  // Scroll to top on route change (both window and #root container)
  useEffect(() => {
    window.scrollTo(0, 0);
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoading) {
      trackPageView(location.pathname, window.location.href);
    }
  }, [location.pathname, isLoading]);

  const marketingPages: Array<{ name: string; path: string }> = [
    { name: 'Licensed Trades', path: '/for-trades' },
    { name: 'Retailers & Product Businesses', path: '/for-retailers' },
    { name: 'Property, Community & Care', path: '/for-property-community-care' },
    { name: 'Hospitality & Venue Groups', path: '/for-hospitality' },
    { name: 'Industry News', path: '/news' },
  ];
  const isFullScreenRoute = location.pathname.startsWith('/admin/ai-analyst') || /^\/admin\/issues(\/|$)/.test(location.pathname);
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const openMobileNav = Boolean(anchorElNav);

  const [anchorElSolutions, setAnchorElSolutions] = React.useState<null | HTMLElement>(null);
  const openSolutionsMenu = Boolean(anchorElSolutions);

  const toolbarRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (toolbarRef.current) {
        if (toolbarRef.current.scrollWidth > toolbarRef.current.clientWidth) {
          console.warn('UI WARNING: Top navigation toolbar is overflowing its boundaries. Menu items may be cut off.');
        }
      }
    };

    // Check on mount and when resizing
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [user]); // Re-check when user auth state changes since it adds/removes elements

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenSolutionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElSolutions(event.currentTarget);
  };

  const handleCloseSolutionsMenu = () => {
    setAnchorElSolutions(null);
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      height: isFullScreenRoute ? '100dvh' : undefined,
      minHeight: isFullScreenRoute ? undefined : '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: isFullScreenRoute ? 'hidden' : undefined
    }}>
      {user?.impersonated_by_admin_id && (
        <Box
          sx={{
            backgroundColor: '#ea580c',
            color: '#ffffff',
            px: { xs: 2, md: 3 },
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1300,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
              ⚠️ IMPERSONATION ACTIVE:
            </Typography>
            <Typography variant="body2">
              Viewing system as <strong>{user.name}</strong> ({user.email}) &middot; Role: <strong>{user.active_role || 'user'}</strong>
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleStopImpersonating}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#dc2626',
              '&:hover': { backgroundColor: '#b91c1c' },
              whiteSpace: 'nowrap',
              ml: 2,
            }}
          >
            Exit Impersonation
          </Button>
        </Box>
      )}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: brand.ink,
          color: brand.paper,
          width: '100%',
          boxShadow: '0 1px 0 rgba(238,242,232,0.08)',
          borderBottom: `1px solid ${brand.darkLine}`,
        }}
      >
        <Toolbar
          ref={toolbarRef}
          disableGutters
          sx={{
            px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
            minHeight: { xs: 56, sm: 64 },
            width: '100%',
          }}
        >
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mr: { xs: 1, md: 2 },
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <Logo tone="onDark" height={29} aria-label="Trade Connector" />
            </Box>

            {!user && <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />}

            {/* Mobile hamburger (marketing pages only, logged-out) */}
            {!user && (
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="contained"
                  size="small"
                  sx={{ 
                    backgroundColor: 'primary.main', 
                    '&:hover': { backgroundColor: 'primary.dark' },
                    whiteSpace: 'nowrap',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Get Started Free
                </Button>
                <Tooltip title="Open navigation menu">
                  <IconButton
                    size="large"
                    aria-label="Open navigation menu"
                    aria-controls={openMobileNav ? 'menu-appbar' : undefined}
                    aria-expanded={openMobileNav ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    sx={{ color: brand.paper, p: { xs: 0.5, sm: 1 } }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
                {openMobileNav && (
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorElNav}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    keepMounted
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={openMobileNav}
                    onClose={handleCloseNavMenu}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                  >
                    {marketingPages.map((page) => (
                      <MenuItem
                        key={page.path}
                        component={RouterLink}
                        to={page.path}
                        onClick={handleCloseNavMenu}
                      >
                        <Typography textAlign="center" sx={{ color: 'text.primary' }}>
                          {page.name}
                        </Typography>
                      </MenuItem>
                    ))}
                    <MenuItem
                      component={RouterLink}
                      to="/about-us"
                      onClick={handleCloseNavMenu}
                      aria-current={isAboutActive ? 'page' : undefined}
                    >
                      <Typography textAlign="center" sx={{ color: 'text.primary' }}>
                        About Us
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleCloseNavMenu();
                        navigate('/login');
                      }}
                    >
                      <Typography textAlign="center" sx={{ color: 'text.primary' }}>
                        Login
                      </Typography>
                    </MenuItem>
                  </Menu>
                )}
              </Box>
            )}

            {user ? (
              <>
                {/* On mobile: hamburger left, actions right. On desktop: nav links fill space */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                  <RoleBasedNavigation />
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1, md: 1 } }}>
                  <RoleSwitcher />
                  <NotificationCenter onViewAll={() => navigate('/notifications')} />
                  <UserProfileMenu />
                </Box>
              </>
            ) : (
              <>
                {/* Desktop marketing dropdown */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
                  <Button
                    id="solutions-menu-button"
                    aria-controls={openSolutionsMenu ? 'solutions-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openSolutionsMenu ? 'true' : undefined}
                    onClick={handleOpenSolutionsMenu}
                    sx={{ my: 2, color: brand.paper, textTransform: 'none', fontSize: '15px', fontWeight: 500, '&:hover': { color: '#fff', backgroundColor: 'rgba(238,242,232,0.08)' } }}
                  >
                    Industries
                    <ArrowDropDownIcon sx={{ ml: 0.5 }} />
                  </Button>
                  <Menu
                    id="solutions-menu"
                    anchorEl={anchorElSolutions}
                    open={openSolutionsMenu}
                    onClose={handleCloseSolutionsMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {marketingPages.map((page) => (
                      <MenuItem
                        key={page.path}
                        component={RouterLink}
                        to={page.path}
                        onClick={handleCloseSolutionsMenu}
                      >
                        <Typography sx={{ color: 'text.primary' }}>{page.name}</Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>

                {/* About Us link */}
                <Button
                  component={RouterLink}
                  to="/about-us"
                  variant="text"
                  aria-current={isAboutActive ? 'page' : undefined}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    my: 2,
                    mr: 1,
                    color: brand.paper,
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: 500,
                    '&:hover': { color: '#fff', backgroundColor: 'rgba(238,242,232,0.08)' },
                  }}
                >
                  About Us
                </Button>

                {/* Auth buttons */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="text"
                    sx={{ color: brand.paper, textTransform: 'none', fontSize: '15px', fontWeight: 500, '&:hover': { color: '#fff', backgroundColor: 'rgba(238,242,232,0.08)' } }}
                  >
                    Login
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/signup"
                    variant="contained"
                    sx={{ backgroundColor: 'primary.main', fontSize: '15px', py: '9px', '&:hover': { backgroundColor: 'primary.dark' } }}
                  >
                    Get Started Free
                  </Button>
                </Box>
              </>
            )}
          </Toolbar>
      </AppBar>

      {isMarketingRoute ? (
        <Box component="main" sx={{ flexGrow: 1 }}>
          {children}
        </Box>
      ) : isFullScreenRoute ? (
        <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 0, minHeight: 0 }}>
          <ComplianceAlertBanner />
          {children}
        </Box>
      ) : (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            py: { xs: 1.5, sm: 2, md: 3 },
            px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
            boxSizing: 'border-box',
          }}
        >
          <ComplianceAlertBanner />
          {children}
        </Box>
      )}

      {isMarketingRoute && <MarketingFooter />}
    </Box>
  );
};

export default Layout;
